import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container } from "reactstrap";
import NGITable from "components/Common/NGITable";
import { firmwareWhitelistColumns } from "pages/settings/firmware-whitelists/FirmwareWhitelistColumns";
import CustomButton from "../../../components/Common/buttons/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";
import { getDictionary } from "store/dictionaries/actions";
import { DICTIONARY_TYPES } from "store/dictionaries/constants";
import NewWhitelistModal from "pages/settings/firmware-whitelists/NewWhitelist/NewWhitelistModal";
import { defaultItemData, MODE } from "pages/settings/firmware-whitelists/constants";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { createDictionary } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";

const FirmwareWhitelistList = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState({
    item: null,
    type: null,
  });

  const {
    list: { firmwareWhitelist, tenant },
  } = useSelector(DictionariesSelector);

  const closeCreateModal = useCallback(() => {
    setIsOpen(false);
    setSelectedItem(defaultItemData);
  }, []);
  const openCreateModal = useCallback(() => setIsOpen(true), []);

  const tenantMapping = useMemo(
    () =>
      Object.entries(tenant || {}).reduce((acc, [name, ids]) => {
        ids.forEach(id => {
          acc[id] = name;
        });
        return acc;
      }, {}),
    [tenant]
  );

  const onEdit = useCallback((item, mode) => {
    setSelectedItem({ item: { ...item, tenant: tenantMapping[item.tenant], tenantId: item.tenant }, mode });
    if (mode === MODE.EDIT) {
      openCreateModal();
    }
  }, []);

  const columns = useMemo(() => firmwareWhitelistColumns(tenantMapping, onEdit), [tenantMapping, onEdit]);

  const updateList = useCallback(() => {
    dispatch(getDictionary(DICTIONARY_TYPES.FIRMWARE_WHITELIST));
  }, []);

  const onSaveDictionary = useCallback(() => {
    const newList =
      selectedItem.mode === MODE.CLEAR
        ? firmwareWhitelist.map(item =>
            item.tenant === selectedItem.item.tenantId && item.depot === selectedItem.item.depot
              ? { ...item, firmwares: [] }
              : item
          )
        : firmwareWhitelist.filter(
            item => item.tenant !== selectedItem.item.tenantId || item.depot !== selectedItem.item.depot
          );

    const formattedData = {
      name: DICTIONARY_TYPES.FIRMWARE_WHITELIST,
      items: newList,
    };
    setIsLoading(true);
    createDictionary(formattedData)
      .then(() => {
        setIsLoading(false);
        successMessage(`Dictionary was ${selectedItem.mode === MODE.CLEAR ? "cleared" : "removed"} successfully`);
        updateList();
      })
      .catch(err => {
        setIsLoading(false);
        defaultNgiErrorMethod(err);
      });
  }, [selectedItem]);

  useEffect(() => {
    dispatch(getDictionary(DICTIONARY_TYPES.TENANT));
    dispatch(getDictionary(DICTIONARY_TYPES.DEPOT));
    updateList();
  }, []);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className="filters-wrapper">
            <div className="filter-buttons">
              <CustomButton
                onClick={openCreateModal}
                icon={<i className="bx bx-plus-circle" />}
                type="primary"
                text="NEW WHITELIST"
              />
            </div>
          </div>
          <Col lg={12}>
            <NGITable
              columns={columns}
              isLoading={isLoading}
              data={{
                result: { items: firmwareWhitelist },
              }}
            />
          </Col>
        </Container>
        {isOpen && (
          <NewWhitelistModal onClose={closeCreateModal} updateList={updateList} selectedItem={selectedItem.item} />
        )}
        {(selectedItem.mode === MODE.CLEAR || selectedItem.mode === MODE.REMOVE) && (
          <ConfirmationModal
            loading={isLoading}
            onCancel={closeCreateModal}
            onConfirm={onSaveDictionary}
            description={
              selectedItem.mode === MODE.CLEAR
                ? "Are you sure you want to clear firmware versions?"
                : "Are you sure you want to remove firmware whitelist?"
            }
          />
        )}
      </div>
    </>
  );
};

export default FirmwareWhitelistList;
